import { useEffect, useMemo } from 'react';

/**
 * PAGE-LEVEL READING (ADR-0056) — what the buyer viewer tells the room admin:
 * which page was on screen, and for how long. Facts only (page numbers, whole
 * seconds), batched so a reader costs about four small requests a minute:
 *
 *   - time accrues to the page in view only while the tab is visible;
 *   - one report every {@link FLUSH_INTERVAL_MS} while there is something to say,
 *     and one when the tab is hidden, the page unloads or the viewer closes
 *     (`keepalive`, so it survives the unload);
 *   - each report carries whole seconds per page (the fraction waits for the
 *     next one) and how many times the reader arrived on the page.
 *
 * Fire-and-forget: a failed or refused report (429, 404) is dropped, never
 * retried and never shown — reading must never be disturbed by it. The server
 * decides what counts (a buyer, a document they can see); this only reports.
 */

export const FLUSH_INTERVAL_MS = 15_000;
/** Pages one report may carry (the server's limit). */
export const MAX_PAGES_PER_REPORT = 50;
/** Pages that may carry seconds in one report (the server's limit). */
export const MAX_TIMED_PAGES_PER_REPORT = 3;

export interface PageReadReport {
  /** The version on screen (the server's `X-Document-Version`). */
  versionId: string;
  pageCount: number;
  pages: { page: number; seconds: number; views: number }[];
}

export interface PageReadTrackerOptions {
  /** The version on screen; every report names it. */
  versionId: string;
  /** Sends one report (default: a keepalive POST to the page-reads route). */
  send: (report: PageReadReport) => void;
  now?: () => number;
  /** Whether the tab is visible now (default: `document.visibilityState`). */
  isVisible?: () => boolean;
}

interface Pending {
  ms: number;
  views: number;
}

export class PageReadTracker {
  private readonly versionId: string;
  private readonly send: (report: PageReadReport) => void;
  private readonly now: () => number;
  private readonly isVisible: () => boolean;
  private page: number | null = null;
  private pageCount = 0;
  /** When time last accrued to `page` (null while hidden). */
  private since: number | null = null;
  private readonly pending = new Map<number, Pending>();

  constructor(options: PageReadTrackerOptions) {
    this.versionId = options.versionId;
    this.send = options.send;
    this.now = options.now ?? (() => Date.now());
    this.isVisible =
      options.isVisible ??
      (() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
  }

  /** The reader is now on `page` of `pageCount`. */
  setPage(page: number, pageCount: number): void {
    if (!Number.isInteger(page) || !Number.isInteger(pageCount) || page < 1 || pageCount < 1) return;
    if (page === this.page && pageCount === this.pageCount) return;
    this.accrue();
    // A new page count, or three pages already waiting with whole seconds (the
    // most one report may carry): report what is due before moving on.
    if ((pageCount !== this.pageCount && this.pageCount !== 0) || this.timedPages() >= 3) {
      this.flush();
    }
    this.page = Math.min(page, pageCount);
    this.pageCount = pageCount;
    this.entry(this.page).views += 1;
    this.since = this.isVisible() ? this.now() : null;
  }

  /** The tab was hidden or shown. Hidden: report what is due now. */
  visibilityChanged(): void {
    if (this.isVisible()) {
      if (this.page !== null && this.since === null) this.since = this.now();
      return;
    }
    this.accrue();
    this.since = null;
    this.flush();
  }

  /** Time passes: accrue and report (the 15-second tick). */
  tick(): void {
    this.accrue();
    this.flush();
  }

  /** The viewer closes / the page unloads: report everything and pause. */
  close(): void {
    this.accrue();
    this.since = null;
    this.flush();
  }

  /** (Re)start the clock on the page in view — the viewer is (again) showing it. */
  start(): void {
    if (this.page !== null && this.since === null && this.isVisible()) this.since = this.now();
  }

  /** Pages waiting with at least one whole second. */
  private timedPages(): number {
    let n = 0;
    for (const e of this.pending.values()) if (e.ms >= 1000) n += 1;
    return n;
  }

  private entry(page: number): Pending {
    let e = this.pending.get(page);
    if (!e) this.pending.set(page, (e = { ms: 0, views: 0 }));
    return e;
  }

  private accrue(): void {
    if (this.page === null || this.since === null) return;
    const t = this.now();
    this.entry(this.page).ms += Math.max(0, t - this.since);
    this.since = t;
  }

  /**
   * Sends whole seconds and arrivals; keeps sub-second remainders for later.
   * Seconds go on at most {@link MAX_TIMED_PAGES_PER_REPORT} pages (the server
   * refuses more); the rest wait for the next report.
   */
  private flush(): void {
    if (this.pageCount < 1) return;
    const pages: PageReadReport['pages'] = [];
    let timed = 0;
    for (const [page, e] of [...this.pending].sort((a, b) => a[0] - b[0])) {
      if (pages.length >= MAX_PAGES_PER_REPORT) break;
      let seconds = Math.floor(e.ms / 1000);
      if (seconds > 0 && timed >= MAX_TIMED_PAGES_PER_REPORT) seconds = 0;
      if (seconds === 0 && e.views === 0) continue;
      if (seconds > 0) timed += 1;
      pages.push({ page, seconds, views: e.views });
      e.ms -= seconds * 1000;
      e.views = 0;
      if (e.ms === 0) this.pending.delete(page);
    }
    if (pages.length > 0) {
      this.send({ versionId: this.versionId, pageCount: this.pageCount, pages });
    }
  }
}

/** POSTs one report, surviving an unload; any failure is ignored. */
export function sendPageReadReport(roomId: string, documentId: string, report: PageReadReport): void {
  try {
    void fetch(`/api/v1/rooms/${roomId}/documents/${documentId}/page-reads`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
    }).catch(() => {
      /* reading is never disturbed by a report */
    });
  } catch {
    /* fetch unavailable: nothing to report */
  }
}

/**
 * The tracker for one open document version, or null when tracking is off (the
 * buyer's own LOI, or a read whose version the server did not name). Ticks every
 * 15 s, reports when the tab is hidden or the page unloads, and once more when
 * the viewer closes.
 */
export function usePageReadTracker(
  roomId: string,
  documentId: string,
  versionId: string | null,
): PageReadTracker | null {
  const tracker = useMemo(
    () =>
      versionId
        ? new PageReadTracker({
            versionId,
            send: (report) => sendPageReadReport(roomId, documentId, report),
          })
        : null,
    [roomId, documentId, versionId],
  );
  useEffect(() => {
    if (!tracker) return;
    tracker.start();
    const timer = setInterval(() => tracker.tick(), FLUSH_INTERVAL_MS);
    const onVisibility = () => tracker.visibilityChanged();
    const onPageHide = () => tracker.close();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      tracker.close();
    };
  }, [tracker]);
  return tracker;
}
